import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, finalize, first, map } from 'rxjs/operators';
import { CompanyResponse } from 'src/app/mobile/login/login.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { Company } from 'src/app/shared/models/company';
import { AdminUser } from 'src/app/shared/models/misc.model';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { TwitterQueueEnum } from 'src/app/shared/strategies/shared/twitter-queue.enum';
import { StrategyAbstractService } from 'src/app/shared/strategies/strategy-abstract.service';
import { DEFAULT_SORT_ORDER_ID, TasksListSettingsHelper } from 'src/app/shared/tasks-list-settings';
import { UserPreferencesNotifications } from 'src/app/user-preferences/shared/models';
import { environment } from 'src/environments/environment';

import { TrackLoggerService } from '../track/track-logger.service';
import { AlcmeonContext, JwtShort, Self, WACloudApiSettings } from './models';
import { SelfV2 } from './models/self-v2.model';

const JWT_SHORT_ENDPOINT_PATH = 'create-temporary-jwt';
const JWT_SHORT_LIFETIME = 5 * 60 * 1000;

const title = 'Alcméon';

const STORAGE_KEY = 'AlcmeonContextService/context';

/**
 * This services stores various attribute of the context in which we call the Alcmeon backend,
 * such as our companyId and the JWT used for authenticating requests to the backend.
 */
@Injectable({
  providedIn: 'root',
})
export class AlcmeonContextService {
  tasksListSettingsHelper: TasksListSettingsHelper;

  preferencesChangedBS = new Subject<void>();
  siteIdBS = new BehaviorSubject<number>(0);
  waCloudApiSettings: WACloudApiSettings;

  private context = new AlcmeonContext();
  private _self?: Self | SelfV2;
  private jwtShort$ = new BehaviorSubject<string>('');
  private requestLock = false;

  constructor(private http: HttpClient, private storage: Storage, private trackLoggerService: TrackLoggerService) {
    this.siteIdBS.subscribe(value => {
      if (value) {
        this.context.siteId = value;
      }
    });
  }

  /**
   * /!\ Be carreful here, when migrating routes in angular.
   * In AJS the token is stored with the id of the company in the name of the key, directly read in url, so no problem when switching company.
   * In NG, the context is stored with an unique key name for all companies, so when switching company,
   * if calls are triggered before the response of self (resolver), the id and token are wrong (from previous company), so errors occur.
   */
  async initFromStoragePromise() {
    const context = await this.storage.get(STORAGE_KEY);
    this.context = context || {};
    this.trackLoggerService.log(`AlcCtx inited context - hasJwt: ${!!this.context.jwt}`);
    if (!this.context.taskListSettings) {
      this.context.taskListSettings = {
        selectedSiteIds: [],
        selectedSiteGroupIds: [],
        selectedChannelIds: [],
        selectedTeamIds: [],
        selectedSortOrderId: DEFAULT_SORT_ORDER_ID,
      };
    }
    if (this.context.taskListSettings.selectedSortOrderId === undefined) {
      // TODO remove this retrocompat code when all devices are >= v65
      this.context.taskListSettings.selectedSortOrderId = DEFAULT_SORT_ORDER_ID;
    }
    this.tasksListSettingsHelper = new TasksListSettingsHelper(this.context.taskListSettings);
    this.tasksListSettingsHelper.settingsChanged$.subscribe(() => this.syncToStorage());
    return this.context;
  }

  clearJwt() {
    this.setJwt(null);
  }

  hasJwt(): boolean {
    return !!this.jwt;
  }

  get jwt(): string {
    return this.context.jwt;
  }

  setJwt(jwt: string) {
    this.context.jwt = jwt;
    this.context.jwtTimestamp = Date.now();
    this.syncToStorage();
  }

  storeCompanyUrlName(companyId, loggedUser) {
    const user = loggedUser;
    if (user) {
      const company = user.companies[0];
      if (company) {
        const { url_name } = company;
        this.storage.set(`${companyId}-url-name`, url_name);
      }
    }
  }

  async getCompanyUrlNameFromStorage(companyId) {
    if (companyId) {
      return await this.storage.get(`${companyId}-url-name`);
    }
    return Promise.resolve();
  }

  /**
   * Add a short lived JWT to an url
   * Used for medias / iframe / file download
   */
  addJwtShortToUrl(src: string, force?: boolean): Observable<string> {
    return this.getJwtShort(force).pipe(
      first(),
      map(jwtShort => {
        const url = new URL(src);
        url.searchParams.set('jwt', jwtShort);
        return url.toString();
      })
    );
  }

  /**
   * Get a jwt short lived, with cache and concurrency management
   */
  private getJwtShort(force?: boolean): Observable<string> {
    // if jwt exist and < lifetime
    if (!force) {
      if (this.context.jwtShort && this.context.jwtShortTimestamp >= Date.now() - JWT_SHORT_LIFETIME) {
        return of(this.context.jwtShort);
      }

      // wait request in progress
      if (this.requestLock) {
        return this.jwtShort$.pipe(filter(jwt => !!jwt && !this.requestLock));
      }
    }

    // first call or renew
    const url = `${API_URL_COMPANIES}/${this.context.companyId}/${JWT_SHORT_ENDPOINT_PATH}`;
    this.requestLock = true;
    return this.http.post<JwtShort>(url, null).pipe(
      map((jwtShort: JwtShort) => {
        this.context.jwtShort = jwtShort.jwt;
        this.context.jwtShortTimestamp = Date.now();
        this.requestLock = false;
        this.jwtShort$.next(jwtShort.jwt);
        this.syncToStorage();
        return jwtShort.jwt;
      }),
      catchError(() => {
        this.jwtShort$.next('');
        return of('');
      }),
      finalize(() => (this.requestLock = false))
    );
  }

  redirectWithJwtShort(url: string) {
    this.addJwtShortToUrl(url).subscribe((urlWithJwt: string) => {
      window.location.href = urlWithJwt;
    });
  }

  get companyId() {
    return this.context.companyId;
  }

  set companyId(companyId) {
    this.context.companyId = companyId;
    this.syncToStorage();
  }

  get companyCode() {
    return this.context.companyCode;
  }

  get isSaml() {
    return this.context.isSaml;
  }

  get samlConfigId() {
    return this.context.samlConfigId;
  }

  get lang() {
    return this.context.lang;
  }

  set lang(lang) {
    this.context.lang = lang;
    this.syncToStorage();
  }

  /**
   * @remarks
   * Be careful when getting siteId.
   * It's used and up to date in chat bot editor, but in processing page it's not updated.
   * It can be the id of the last site edited in chat bot editor,
   * which is potentially different from the site of the displayed task.
   */
  get siteId(): number {
    return this.context.siteId;
  }

  get self() {
    return this._self;
  }

  set self(self) {
    self.companies[0].usersMap =
      (self.companies[0].users || []).reduce((result, user) => {
        result.set(user.id, user);
        return result;
      }, new Map()) || new Map();
    this._self = self;
  }

  get notificationsDetails() {
    return this._self.notifications_details;
  }

  set notificationsDetails(details: UserPreferencesNotifications) {
    this._self.notifications_details = details;
  }

  isEngagement() {
    return this.getCompany().isEngagement;
  }

  isMe(adminUser) {
    return adminUser && adminUser.id === this.self.id;
  }

  getCompany(): Company {
    // companies has always one element, if for some reason this.context.companyId was not set
    // before calling this method, we fallback to first element of companies
    return this.self.companies[0];
  }

  getThirdPartyCrmIFrameConfigs(siteId) {
    const configs = this.getCompany().third_party_crm_iframes.filter(i => i.sites_ids.includes(siteId));
    return configs;
  }

  isInternalCompany() {
    return this.getCompany().flavor === 'internal';
  }

  isSpellCheckerEnabledForCompany() {
    const companySettings = this.getCompany().settings;
    return companySettings?.enable_spellchecker && environment.bundledWithAngularJSApp;
  }

  isLoggedUserPartOfTeam(tagId) {
    // return true if no routing tag is defined for the company (or if somehow the DataFormatter's behaviour has changed)
    return this.self.routing_tags ? this.self.routing_tags?.map(tag => tag.id).includes(tagId) : true;
  }

  isScenarioBot(userId) {
    return this.getCompany().scenario_bot_ids.includes(userId);
  }

  isBot(userId) {
    const company = this.getCompany();
    const botIds = [...company.scenario_bot_ids, company.incoming_messages_bot_id, company.default_bot_id];
    return botIds.includes(userId);
  }

  canLoggedUserSeeActionErrorDetails() {
    return this.isAlcmeonUser();
  }

  isAlcmeonUser() {
    return this.self?.email?.endsWith('@alcmeon.com');
  }

  isOnlyMarketingCompany() {
    const { marketing, care } = this.self;
    return marketing && !care;
  }

  setCompanyInfo(info: CompanyResponse) {
    this.context.companyId = info.id;
    this.context.companyCode = info.url_name;
    this.context.isSaml = info.uses_saml;
    if (info.uses_saml) {
      try {
        this.context.samlConfigId = info.login_options[0].saml_configuration_id;
      } catch {
        // Do nothing.
      }
    }
    this.syncToStorage();
  }

  setCompanyNameInTabTitle(name: string) {
    document.querySelector('title').innerText = title + '-' + name;
  }

  // Updates username, lang or timezone on context after a change on the preferences page
  updatePreference(pref: string, value: string) {
    this.self[pref] = value;
    this.preferencesChangedBS.next();
  }

  getColleague(id: number): AdminUser {
    const result = {
      id,
      name: 'N/A',
      email: 'N/A',
    };
    const user = this.getCompany().usersMap.get(id);
    if (!user) {
      return result;
    }
    return user;
  }

  private syncToStorage() {
    this.storage.set(STORAGE_KEY, this.context);
  }

  isLoggedUserAdmin() {
    return this.self.is_company_admin;
  }

  /**
   * Determines if the user has only the read_only permission, and not any other permission, for a given site
   */
  isLoggedUserReadOnlyForSite(siteId: number): boolean {
    return (
      !this.self.capabilities ||
      (this.self.capabilities[siteId]?.includes(CapabilityIdEnum.ReadOnly) &&
        this.self.capabilities[siteId].length === 1)
    );
  }

  /**
   * Migrated from user.services.js
   */
  getAllowedSites(sitesV2Map): SiteV2[] {
    const user = this.self;
    if (!user) {
      return [];
    }
    if (user.capabilities) {
      return Object.keys(user.capabilities)
        .filter(siteId => user.capabilities[siteId]?.includes(CapabilityIdEnum.Answer))
        .map(siteId => sitesV2Map.get(+siteId))
        .filter(s => s);
    }
    return [];
  }

  hasCapability(siteId: number, capabilityId: number) {
    const user = this.self;

    if (user?.capabilities && siteId) {
      return user.capabilities[siteId]?.includes(capabilityId);
    }
    return false;
  }

  /**
   * @deprecated remove after olympic games
   */
  addPublicRepliesTaskIfAllowed(strategy: StrategyAbstractService, tasks: number[]) {
    // Only for RATP and Alcmeon and for Twitter.
    if (
      strategy.getTypeString() === SiteTypeEnum.Twitter &&
      ([146, 252].includes(this.companyId) || 'alcmeon' === this.companyCode)
    ) {
      (tasks || []).push(TwitterQueueEnum.PUBLIC_REPLIES);
    }
  }
}
